import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Upload, X } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { inputCls, labelCls, btnPrimary, PanelHeader } from "./_shared";
import { fileToDataUrl } from "./GenericListPanel";

export default function AboutPanel() {
  const { adminApi } = useAuth();
  const { refresh } = useContent();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    adminApi.get("/content").then((r) => setForm({ ...r.data.about })).catch(() => toast.error("Failed to load"));
  }, [adminApi]);

  if (!form) return <div className="text-white/50">Loading…</div>;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePhotoFile = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    try {
      const dataUrl = await fileToDataUrl(f, 1600, 0.85);
      setF("photo_data", dataUrl);
      setF("photo_url", "");
    } catch (e) {
      toast.error(e.message || "Photo upload failed");
    }
  };

  const clearPhoto = () => {
    setF("photo_data", null);
    setF("photo_url", "");
    if (fileRef.current) fileRef.current.value = "";
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/content/about", {
        eyebrow: form.eyebrow,
        owner_name: form.owner_name,
        owner_title: form.owner_title,
        years: Number(form.years) || 0,
        intro: form.intro,
        quote: form.quote,
        quote_author: form.quote_author,
        photo_url: form.photo_url || "",
        photo_data: form.photo_data || null,
      });
      toast.success("About section updated");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const preview = form.photo_data || form.photo_url;

  return (
    <div data-testid="about-panel">
      <PanelHeader
        eyebrow="02"
        title="About Hondo"
        actions={
          <button data-testid="about-save" onClick={save} disabled={saving} className={btnPrimary}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        }
      />

      <div className="space-y-6">
        <div>
          <label className={labelCls}>Owner Photo (portrait recommended — will display as a large image in the About section)</label>
          <div className="border border-white/10 bg-[#1A1D21] p-4 space-y-3">
            {preview ? (
              <div className="relative w-full max-w-xs">
                <img
                  src={preview}
                  alt="Owner portrait preview"
                  data-testid="about-photo-preview"
                  className="w-full aspect-[3/4] object-cover border border-white/10"
                />
                <button
                  type="button"
                  onClick={clearPhoto}
                  data-testid="about-photo-clear"
                  className="absolute top-2 right-2 bg-black/80 hover:bg-red-500 text-white p-2 border border-white/20 transition-colors"
                  aria-label="Clear photo"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="w-full max-w-xs aspect-[3/4] border border-dashed border-white/20 flex items-center justify-center text-white/40 text-sm text-center px-4">
                No photo — the About section shows text only
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              data-testid="about-photo-file"
              onChange={handlePhotoFile}
              className="block w-full text-sm text-white/70 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#FF6B00] file:text-white file:font-display file:uppercase file:tracking-widest file:text-xs hover:file:bg-[#E65A00] cursor-pointer"
            />
            <div className="text-xs text-white/40 flex items-center gap-2">
              <Upload size={12} /> Or paste an image URL:
            </div>
            <input
              className={inputCls}
              data-testid="about-photo-url"
              placeholder="https://…"
              value={form.photo_url || ""}
              onChange={(e) => {
                setF("photo_url", e.target.value);
                if (e.target.value) setF("photo_data", null);
              }}
            />
          </div>
        </div>

        <div>
          <label className={labelCls}>Eyebrow</label>
          <input className={inputCls} value={form.eyebrow || ""} onChange={(e) => setF("eyebrow", e.target.value)} />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Owner Name</label>
            <input className={inputCls} value={form.owner_name || ""} onChange={(e) => setF("owner_name", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Owner Title</label>
            <input className={inputCls} value={form.owner_title || ""} onChange={(e) => setF("owner_title", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Years of Experience</label>
            <input type="number" className={inputCls} value={form.years || 0} onChange={(e) => setF("years", e.target.value)} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Intro Paragraph</label>
          <textarea
            data-testid="about-intro"
            className={`${inputCls} min-h-[160px]`}
            value={form.intro || ""}
            onChange={(e) => setF("intro", e.target.value)}
          />
          <p className="text-xs text-white/40 mt-2">
            Tip — press Enter twice to create a blank line and split the intro into separate paragraphs on the site.
          </p>
        </div>
        <div>
          <label className={labelCls}>Featured Quote</label>
          <textarea className={`${inputCls} min-h-[100px]`} value={form.quote || ""} onChange={(e) => setF("quote", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Quote Author</label>
          <input className={inputCls} value={form.quote_author || ""} onChange={(e) => setF("quote_author", e.target.value)} />
        </div>
      </div>
    </div>
  );
}
